import React from 'react';
import {
  AccordionListItem,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Separator,
  TruncatedText,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import {
  PiCircle,
  PiDotsThreeVertical,
  PiLighthouse,
  PiQuestion,
} from 'react-icons/pi';
import { AtonHost } from '@ais-ui/api';
import { useLocalization } from '@ais-ui/locale-management';
import { ShowAtonButton } from './ShowAtonButton';
import {
  DetailView,
  useViewMode,
  ViewMode,
} from '../../../components/ViewModeProvider';
import { noop } from 'lodash';
import { useSelectionState } from '../../../components/SelectionStateProvider';
import { HostAtonItemContent } from './HostAtonItemContent';

interface HostAtonItemProps {
  aton: AtonHost;
  requiresMap: boolean;
}

export const HostAtonItem = ({ aton, requiresMap }: HostAtonItemProps) => {
  const { messages } = useLocalization();

  const { setViewMode } = useViewMode();

  const { setAton } = useSelectionState();

  // Determine icon color based on service states
  const getIconColor = (aton: AtonHost): string => {
    if (!aton.serviceStates) {
      return 'text-muted-foreground'; // Default color if no service states
    }

    // Red if any critical services
    if (aton.serviceStates.critical > 0) {
      return 'text-red-500';
    }

    // Yellow if any warning services but no critical
    if (aton.serviceStates.warning > 0) {
      return 'text-yellow-500';
    }

    // Green if all services are OK (and there are services)
    if (aton.serviceStates.ok > 0) {
      return 'text-green-500';
    }

    // Gray for unknown/pending states
    return 'text-muted-foreground';
  };

  const getIconForSiteType = (
    siteType: string | undefined,
    iconColor: string,
  ) => {
    const baseIcon = (() => {
      if (!siteType) {
        return PiQuestion;
      }

      switch (siteType.toLowerCase()) {
        case 'lighthouse':
          return PiLighthouse;
        case 'buoy':
          return PiCircle;
        case 'aton':
          return PiCircle;
        default:
          return PiQuestion;
      }
    })();

    const IconComponent = baseIcon;
    return <IconComponent className={cn('size-4', iconColor)} />;
  };

  const onGoToDetails = () => {
    setAton(aton);
    setViewMode(ViewMode.Detail, DetailView.Aton);
  };

  const iconColor = getIconColor(aton);

  return (
    <AccordionListItem
      key={aton.id}
      value={aton.id as string}
      className="pr-2"
      actionButtons={
        <>
          <ShowAtonButton onApply={noop} isApplied={false} disabled={true} />
          <Separator orientation="vertical" className="h-6 bg-border" />
        </>
      }
      triggerContent={
        <>
          <span className="flex-shrink-0">
            {getIconForSiteType(aton.siteType, iconColor)}
          </span>
          <div className="w-full">
            <TruncatedText
              text={aton.title || ''}
              className="text-sm text-left font-medium leading-none group-hover:underline"
              maxWidth="180px"
            />
          </div>
        </>
      }
      kebabMenu={
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              id={`${aton.title?.toLowerCase()}-dropdown-button`}
              size="icon"
              variant="ghost"
              className="size-7"
            >
              <PiDotsThreeVertical className="size-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={onGoToDetails}>
            <DropdownMenuItem>{messages.atonListDetailAction}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      }
    >
      <HostAtonItemContent aton={aton} />
    </AccordionListItem>
  );
};
